import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import {
  checkNewVersion,
  forceUpdate,
  UpdateModal,
} from '@fundacao/shared-components/esm/ie';
import { useLoginContext } from 'context/login-context';
import { CompleteUpdate, IframeUpdate } from 'models/login';

const SHOULD_CHECK_UPDATES = process.env.NODE_ENV === 'production';

const CheckUpdates: FC = () => {
  const { canCheckLogin, redirectURL, setIsLoading } = useLoginContext();

  const [completeUpdates, setCompleteUpdates] = useState<CompleteUpdate[]>([]);
  const [hasUpdate, setHasUpdate] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [registration, setRegistration] = useState<any>();
  const [updates, setUpdates] = useState<IframeUpdate[]>([]);

  const concessao = useRef<HTMLIFrameElement>(null);
  const gb = useRef<HTMLIFrameElement>(null);
  const renovacao = useRef<HTMLIFrameElement>(null);

  const childrenOrigin = window.location.origin;

  const sendMessage = (
    ref: RefObject<HTMLIFrameElement> | null,
    message: any,
  ) => {
    const targetWindow = ref?.current?.contentWindow;
    if (!targetWindow) return;

    targetWindow.postMessage(message, childrenOrigin);
  };

  const returnDash = (dashName: string) => {
    switch (dashName) {
      case 'gestaoBeneficiario':
        return gb;
      case 'renovacao':
        return renovacao;
      case 'concessao':
        return concessao;
      default:
        return null;
    }
  };

  const didUpdateFail = () => {
    setTimeout(() => {
      if (!hasUpdate || !isOpenModal) {
        setIsLoading(false);
      }
    }, 5000);
  };

  const update = () => {
    updates.forEach((updateItem) => {
      if (updateItem.hasUpdate) {
        sendMessage(returnDash(updateItem.target), 'forceUpdate');
      }
    });

    if (!SHOULD_CHECK_UPDATES) return;

    if (hasUpdate) {
      forceUpdate(registration, true);
    }

    didUpdateFail();
  };

  const handleMessage = (event: MessageEvent) => {
    if (event.origin !== childrenOrigin) return;
    if (event.data?.hasUpdate !== undefined) {
      setUpdates((prevUpdates) => [...prevUpdates, event.data]);
    }
    if (event.data?.message) {
      setCompleteUpdates((prevCompleteUpdates) => [
        ...prevCompleteUpdates,
        event.data,
      ]);
    }
  };

  const checkUpdates = async () => {
    const parentUpdate = await checkNewVersion(setRegistration, setHasUpdate);
    const withUpdate = updates.filter((element) => element.hasUpdate === true);
    const canOpenUpdate = updates.some((element) => element.hasUpdate === true);

    if (canOpenUpdate) {
      setIsOpenModal(true);
    }

    if (completeUpdates.length !== withUpdate.length || parentUpdate) return;
    setIsLoading(false);
  };

  useEffect(() => {
    if (!SHOULD_CHECK_UPDATES) return;

    if (hasUpdate) {
      setIsOpenModal(true);
    }
  }, [hasUpdate]);

  useEffect(() => {
    if (!SHOULD_CHECK_UPDATES) return;
    if (updates.length !== 3) return;

    checkUpdates();
  }, [updates]);

  useEffect(() => {
    if (canCheckLogin && redirectURL) {
      window.location.href = redirectURL;
      window.removeEventListener('message', handleMessage, false);
    }
  }, [canCheckLogin, redirectURL]);

  useEffect(() => {
    if (!SHOULD_CHECK_UPDATES) return undefined;

    checkNewVersion(setRegistration, setHasUpdate);
    window.addEventListener('message', handleMessage, false);

    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, []);

  if (!SHOULD_CHECK_UPDATES) return null;

  return (
    <div>
      <UpdateModal
        setIsOpen={setIsOpenModal}
        isOpen={isOpenModal}
        onBtnClicked={update}
      />

      <iframe
        ref={gb}
        id="dash-gestao"
        title="Atualizacao gestao beneficiario"
        style={{ display: 'none' }}
        src={`${process.env.REACT_APP_PORTAL_IE_URL}/gestao-beneficiario/ok`}
        onLoad={() => {
          sendMessage(gb, 'update?');
        }}
      />
      <iframe
        ref={concessao}
        id="dash-concessao"
        title="Atualizacao concessao"
        style={{ display: 'none' }}
        src={`${process.env.REACT_APP_PORTAL_IE_URL}/d/ok`}
        onLoad={() => {
          sendMessage(concessao, 'update?');
        }}
      />
      <iframe
        ref={renovacao}
        id="dash-renovacao"
        title="Atualizacao renovacao"
        style={{ display: 'none' }}
        src={`${process.env.REACT_APP_PORTAL_IE_URL}/renovacao/ok`}
        onLoad={() => {
          sendMessage(renovacao, 'update?');
        }}
      />
    </div>
  );
};

export default CheckUpdates;
